/**
 * CORE-1020 — выбор пути до хаба ChatApi, общий для всех его сокетов.
 *
 * Сокетов два: канал переписки ({@link ./chatApiChannel}) и общий сокет
 * присутствия ({@link ./presenceHub}). Оба ходят на один и тот же хаб и оба
 * умеют идти либо прямым адресом, либо через РФ-прокси. Пока каждый решал сам,
 * у них были СВОИ флаги: у канала — липкость и пометка о вставшем пути, у
 * присутствия — только липкость.
 *
 * Это неверно по существу. Путь один и тот же, и то, что он перестал качать
 * байты, — свойство пути, а не сокета, который это первым заметил. Общая память
 * означает, что обжёгшийся канал уводит с плохого пути и присутствие тоже, и
 * наоборот.
 *
 * Различие, которое здесь важнее всего: «сюда не пускают» и «поднялось и
 * замолчало» — разные отказы. Первый — ровно тот случай, ради которого прокси и
 * заведён (CORE-407: у части РФ-провайдеров прямой сокет висит), и отказываться
 * от него нельзя. Второй — задушенный туннель, и путь надо запомнить.
 */

/** Прокси уже приводил к рабочему соединению в этой сессии. */
let sticky = false;

/**
 * CORE-407 — какой путь замолчал последним.
 *
 * Прежде запоминался только срыв ПРОКСИ, а срыв прямого адреса не записывался
 * никуда. Цена этого измерена из России 15.09.2026: прямой хаб поднимает
 * DNS/TCP/TLS/WS/SignalR, отдаёт первый ответ и глохнет после двух обменов на
 * 5,8 КБ; РФ-релей в том же прогоне проходит все десять сообщений, живёт 159
 * секунд и принимает 43,1 КБ. То есть запасной путь работает — а клиент на него
 * не уходил, потому что после срыва прямого пути перезапуск снова выбирал
 * прямой. Снаружи это «чат завис намертво»: два сообщения, тишина, и так по
 * кругу.
 *
 * Помним ПОСЛЕДНИЙ сорвавшийся путь, а не «был ли срыв»: после срыва пробуется
 * ДРУГОЙ. Прямой встал — идём прокси (случай выше). Прокси встал — идём прямым
 * (так было и раньше). Оба глохнут — чередуем, а не прибиваемся к одному
 * навсегда: мигание неприятно, но мёртвый путь до перезагрузки страницы хуже.
 */
let lastStalled: 'direct' | 'proxy' | null = null;

/** Когда поставлена пометка выше (по часам вызывающего, см. `markHubPathStalled`). */
let lastStalledAt = 0;

/**
 * CORE-1494 — сколько живёт пометка о срыве.
 *
 * Пометка была пожизненной: её ставил `markHubPathStalled`, а снимал только
 * `_resetHubPathForTest`, то есть на деле — перезагрузка вкладки. Состояние
 * модульное (в этом и смысл CORE-1020: путь общий), поэтому ОДНА разовая
 * неудача ОДНОГО канала до конца сессии уводила на противоположный путь и
 * канал переписки, и сокет присутствия. Самый частый вид этой неудачи —
 * не «страна блокирует», а «на минуту пропал wifi» или «стенд
 * перезапускался»: причина прошла, а решение осталось навсегда.
 *
 * Две минуты выбраны по лестнице переподключения
 * ({@link ../../utils/hubReconnectBackoff}): её потолок — 30 с, значит за 2
 * минуты укладывается весь залп повторов одного инцидента (0, 2, 5, 10, 20,
 * 30, 30 с — это ~97 с). Пометка обязана пережить его целиком, иначе починка
 * ходит по кругу: истекла между попытками — и очередная снова выбирает тот же
 * вставший путь. И при этом она не должна переживать инцидент: любая пауза
 * человека в переписке (отошёл, свернул вкладку, вернулся через час) заметно
 * длиннее двух минут, так что к следующему заходу выбор пути снова делается по
 * решению зонда (CORE-45), а не по старой обиде.
 *
 * Меньше (30–60 с) — риск истечь посреди лестницы. Больше (10+ минут) — это
 * снова «до конца сессии» для коротких визитов.
 */
export const HUB_PATH_STALL_TTL_MS = 120_000;

/**
 * Снять пометку, если она просрочена.
 *
 * Отрицательный возраст тоже считаем поводом забыть: часы могли прыгнуть назад
 * (перевод времени, синхронизация NTP после сна вкладки), и лучше потерять
 * пометку, чем сделать её вечной случайной арифметикой.
 */
function forgetExpiredStall(now: number): void {
  if (lastStalled === null) return;
  const age = now - lastStalledAt;
  if (age >= HUB_PATH_STALL_TTL_MS || age < 0) lastStalled = null;
}

export interface HubPathChoice {
  /** Адрес, к которому подключаться. */
  base: string;
  /** Это прокси, а не прямой адрес. */
  usingProxy: boolean;
  /** Есть ли вообще запасной путь. */
  canFallback: boolean;
}

/**
 * Куда подключаться сейчас.
 *
 * `ruFirst` — решение стартового зонда (CORE-45): РФ-пользователь по умолчанию
 * идёт прокси, чтобы не платить таймаутом за прямой адрес. Пометка о вставшем
 * пути это решение перебивает: мигающий чат хуже одной паузы.
 *
 * `now` — часы вызывающего; параметром, а не `Date.now()` в теле, чтобы тест
 * про срок жизни пометки не зависел от системного времени (так же сделан
 * `random` у `withJitter` в `../../utils/hubReconnectBackoff`).
 */
export function chooseHubBase(
  primary: string,
  proxy: string,
  ruFirst: boolean,
  now: number = Date.now(),
): HubPathChoice {
  forgetExpiredStall(now);
  const canFallback = !!proxy && proxy !== primary;
  const preferProxy = canFallback
    && lastStalled !== 'proxy'
    && (sticky || ruFirst || lastStalled === 'direct');
  const base = preferProxy ? proxy : primary;
  return { base, usingProxy: canFallback && base === proxy, canFallback };
}

/**
 * Соединение по этому пути поднялось и замолчало.
 *
 * Зовётся там, где отказ виден, — у ожидания ответа хаба, а не у каждого
 * вызывающего: иначе пометка расходится, и очередной новый вызов про неё
 * забывает.
 */
export function markHubPathStalled(usingProxy: boolean, now: number = Date.now()): void {
  lastStalled = usingProxy ? 'proxy' : 'direct';
  lastStalledAt = now;
}

/**
 * CORE-1494 — этот путь только что доказал, что работает: по нему пришла
 * история.
 *
 * Пара к `markHubPathStalled` — ровно такая же, как
 * `clearHubProxySticky` к `markHubProxyWorking`. Срок жизни пометки спасает от
 * «навсегда», но ждать его незачем, когда уже есть прямое доказательство: путь
 * качает байты. Снимаем только пометку О ТОМ ЖЕ пути: если замолчал прокси, а
 * доказал себя прямой, память о сорвавшемся прокси нужна и дальше — иначе
 * следующее открытие канала снова уйдёт на него.
 */
export function clearHubPathStalled(usingProxy: boolean): void {
  if (lastStalled === (usingProxy ? 'proxy' : 'direct')) {
    lastStalled = null;
    lastStalledAt = 0;
  }
}

/** Прокси довёл до истории — на него можно уводить и остальные сокеты. */
export function markHubProxyWorking(): void {
  sticky = true;
}

/**
 * Липкость снимается, когда путь перестал работать.
 *
 * Пометка о срыве — отдельная вещь и этой функцией не трогается: липкость
 * отвечает на «куда уводить остальные сокеты», пометка — на «куда НЕ идти».
 * Снимается она своей парой (`clearHubPathStalled`) либо по сроку
 * (`HUB_PATH_STALL_TTL_MS`).
 */
export function clearHubProxySticky(): void {
  sticky = false;
}

/** Только для тестов: флаги живут в модуле и переживают экземпляры. */
export function _resetHubPathForTest(): void {
  sticky = false;
  lastStalled = null;
  lastStalledAt = 0;
}
